"use client";

import SearchSuggestionsPopover from "./SearchSuggestionsPopover";
import React, { useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import { Search, X } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { useSearchParams } from "next/navigation";
import { GLASS_INPUT_STYLE, SearchLogType } from "./global-search.type";
import { parseAsString, useQueryState } from "nuqs";
import { Popover, PopoverAnchor } from "@/components/ui/popover";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useGlobalSearchSuggestions,
  useGlobalRealtimeSearch,
  useSearchItemHref,
  useGlobalTrendingSearch,
} from "@/hooks/useGlobalSearch";
import {
  AddSearchLogParams,
  handleDeleteGlobalSearchHistoryAction,
  handleGlobalSearchAddLogAction,
} from "@/actions/global-search.action";

type GlobalSearchProps = {
  wrapperClassName?: string;
  inputClassName?: string;
  isScrolled?: boolean;
};

const GlobalSearch = ({
  wrapperClassName = "lg:max-w-[500px]",
  inputClassName,
  isScrolled = false,
}: GlobalSearchProps) => {
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const locationId = searchParams.get("locationId") ?? "";
  const source = searchParams.get("source") ?? "";
  const isDestination = !!locationId && source === "destination";
  const getHref = useSearchItemHref();

  // State
  const [isFocused, setIsFocused] = useState(false);
  const [submittedKeyword, setSubmittedKeyword] = useState("");
  const [search, setSearch] = useQueryState(
    "gsearch",
    parseAsString.withOptions({ clearOnDefault: true }),
  );
  const value = search ?? "";
  const debouncedSearchValue = useDebounce(value, 300);
  const isSearching = !!debouncedSearchValue.trim();
  // hooks
  const { data: suggestionData, isLoading: suggestionsLoading } =
    useGlobalSearchSuggestions(isFocused && !submittedKeyword);
  const { data: realtimeSearchData, isLoading: isSearchLoading } =
    useGlobalRealtimeSearch(
      debouncedSearchValue,
      isFocused && !!debouncedSearchValue.trim(),
    );
  const { data: trendingData, isLoading: isTrendingLoading } =
    useGlobalTrendingSearch(
      Number(locationId) ?? null,
      isFocused && isDestination,
    );

  const invalidateSearchSuggestions = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ["global-search-suggestions"],
      }),
    [queryClient],
  );

  const handleExecuteSearch = (targetKeyword?: string) => {
    const keywordToSearch = (targetKeyword ?? value).trim();
    if (!keywordToSearch) return;

    setIsFocused(false);
    const url = `/global-search?gsearch=${encodeURIComponent(keywordToSearch)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    invalidateSearchSuggestions();
  };

  // Mutations
  const handleClearHistorySearchMutation = useMutation({
    mutationFn: handleDeleteGlobalSearchHistoryAction,
    onSuccess: () => {
      setSearch(null);
      setSubmittedKeyword("");
      invalidateSearchSuggestions();
    },
  });

  const handleAddSearchLogMutation = useMutation({
    mutationFn: (params: AddSearchLogParams) =>
      handleGlobalSearchAddLogAction(params),
    onSuccess: () => invalidateSearchSuggestions(),
  });

  const handleNavigate = useCallback(
    (item: { id?: string | number; type?: string; label?: string }) => {
      if (!item.type) {
        const url = `/global-search?gsearch=${encodeURIComponent(item.label ?? "")}`;
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      handleAddSearchLogMutation.mutate({
        id: Number(item.id),
        type: item.type as SearchLogType,
      });

      let href = getHref({ id: item.id!, type: item.type });
      if (!href) return;

      if (item.type === "location") {
        href += href.includes("?")
          ? "&source=destination"
          : "?source=destination";
      }

      window.open(href, "_blank", "noopener,noreferrer");
    },
    [getHref, handleAddSearchLogMutation],
  );

  return (
    <div className={cn("relative w-full", wrapperClassName)}>
      <label htmlFor="global-search" className="sr-only">
        Search
      </label>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleExecuteSearch();
        }}
      >
        <Popover open={isFocused} onOpenChange={setIsFocused}>
          <PopoverAnchor asChild>
            <input
              id="global-search"
              type="text"
              value={value}
              placeholder="Search for your next adventure"
              onFocus={() => setIsFocused(true)}
              onClick={() => setIsFocused(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleExecuteSearch();
                }
              }}
              onChange={(e) => {
                const nextValue = e.target.value;
                setSearch(nextValue === "" ? null : nextValue);
                if (submittedKeyword) {
                  setSubmittedKeyword("");
                }
              }}
              style={isScrolled ? undefined : GLASS_INPUT_STYLE}
              className={cn(
                "h-11 w-full rounded-full pl-4 pr-18 text-sm transition-colors",
                isScrolled
                  ? "border border-gray-200 text-[#2f2f2f] placeholder:text-gray-400 focus:border-primary focus:ring-black/5"
                  : "border-0 text-white placeholder:text-white/60 focus:ring-2 focus:ring-white/20",
                "focus:outline-none",
                inputClassName,
              )}
            />
          </PopoverAnchor>

          <SearchSuggestionsPopover
            items={suggestionData}
            realtimeItems={realtimeSearchData}
            trendingItems={trendingData}
            isDestination={isDestination}
            isSearching={isSearching}
            isLoading={
              suggestionsLoading || isSearchLoading || isTrendingLoading
            }
            onNavigate={handleNavigate}
            onClear={() => handleClearHistorySearchMutation.mutate()}
          />
        </Popover>

        <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {/* Clear Button */}
          <button
            type="button"
            onClick={() => {
              setSearch(null);
              setSubmittedKeyword("");
            }}
            aria-label="Clear search"
            className={cn(
              "p-1 transition-opacity",
              value ? "opacity-100" : "pointer-events-none opacity-0",
              isScrolled
                ? "text-gray-400 hover:text-gray-600"
                : "text-white/70 hover:text-white",
            )}
          >
            <X className="h-4 w-4" />
          </button>

          {/* Search Button Keeps Popover Open */}
          <button
            type="submit"
            aria-label="Submit search"
            className="flex items-center justify-center rounded-full bg-primary p-2.5 transition-colors hover:opacity-90 cursor-pointer"
          >
            <Search className="h-4 w-4 text-heading" aria-hidden="true" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default React.memo(GlobalSearch);
